import { Link } from "react-router-dom";
import { Check, MapPin } from "lucide-react";
import { Item, fileUrl, formatMoney } from "@/lib/catalog-api";

interface ItemCardProps {
  item: Item;
  index?: number;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  locationName?: string;
}

export function ItemCard({ item, index = 0, selectable = false, selected = false, onToggleSelect, locationName }: ItemCardProps) {
  const primary = item.photos.find((p) => p.is_primary) || item.photos[0];
  const photoUrl = fileUrl(primary?.url);

  const inner = (
    <>
      <div className="aspect-[4/5] bg-bone overflow-hidden relative">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={item.name}
            loading="lazy"
            className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-muted-foreground text-xs uppercase tracking-[0.25em]">
            No photo
          </div>
        )}
        {selectable && (
          <span className={`absolute top-2 left-2 w-6 h-6 grid place-items-center border ${
            selected ? "bg-oxblood border-oxblood text-ivory" : "bg-background/90 border-border text-transparent"
          }`}>
            <Check className="w-3.5 h-3.5" />
          </span>
        )}
      </div>
      <div className="p-4 space-y-2">
        <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground truncate">
          {item.brand || item.category}
        </p>
        <p className="font-display text-base leading-snug line-clamp-2 min-h-[2.6em]">
          {item.name}
        </p>
        {(locationName || item.location_type) && (
          <p className="text-[11px] text-muted-foreground truncate flex items-center gap-1" data-testid={`item-card-location-${index}`}>
            <MapPin className="w-3 h-3 shrink-0 text-oxblood/70" />
            {[locationName, item.location_type].filter(Boolean).join(" · ")}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          {item.size && (
            <span className="text-[11px] px-2 py-0.5 border border-border bg-bone/40">{item.size}</span>
          )}
          <span className={`text-[11px] px-2 py-0.5 border ${
            item.condition === "Excellent" || item.condition === "New with tags"
              ? "border-oxblood/30 text-oxblood"
              : "border-border text-muted-foreground"
          }`}>
            {item.condition}
          </span>
          {item.insured_value || item.retail_value ? (
            <span className="text-[11px] px-2 py-0.5 border border-border text-muted-foreground ml-auto">
              {formatMoney(item.insured_value || item.retail_value)}
            </span>
          ) : null}
        </div>
      </div>
    </>
  );

  if (selectable) {
    return (
      <button
        type="button"
        data-testid={`item-card-${index}`}
        onClick={() => onToggleSelect?.(item._id)}
        className={`group block text-left bg-background border transition-colors ${
          selected ? "border-oxblood ring-2 ring-oxblood/20" : "border-border hover:border-oxblood/40"
        }`}
      >
        {inner}
      </button>
    );
  }

  return (
    <Link
      to={`/app/items/${item._id}`}
      data-testid={`item-card-${index}`}
      className="group block bg-background border border-border hover:border-oxblood/40 transition-colors"
    >
      {inner}
    </Link>
  );
}
